<template>
    <div
        ref="container"
        class="zoomable-container"
        @mousedown="onMouseDown"
        @wheel.prevent="onWheel"
        :class="[
            props.class,
            {
                'exceeds-left': exceedsLeft,
                'exceeds-right': exceedsRight,
                'exceeds-top': exceedsTop,
                'exceeds-bottom': exceedsBottom,
            },
        ]"
    >
        <div ref="contentContainer" class="zoomable-content" :class="{ dragging: isDragging }" :style="transformStyle">
            <slot></slot>
        </div>

        <VButton class="reset-btn warning" :class="{ visible: isInitialized && isChanged }" @click="reset">
            {{ translations.general.reset }}
        </VButton>
    </div>
    <teleport v-if="SHOW_DEBUG" to="body">
        <div class="zoomable-debug">
            <div>
                <div>translateX: {{ translateX }}</div>
                <div>translateY: {{ translateY }}</div>
                <div>scale: {{ scale }}</div>

                <div>lastScale: {{ lastScale }}</div>
                <div>lastTranslateX: {{ lastTranslateX }}</div>
                <div>lastTranslateY: {{ lastTranslateY }}</div>

                <div>minTranslate.x: {{ minTranslate.x }}</div>
                <div>maxTranslate.x: {{ maxTranslate.x }}</div>

                <div>minTranslate.y: {{ minTranslate.y }}</div>
                <div>maxTranslate.y: {{ maxTranslate.y }}</div>

                <div>isDragging: {{ isDragging }}</div>
            </div>
            <div>
                <div>containerRect.w: {{ containerRect?.width }}</div>
                <div>containerRect.h: {{ containerRect?.height }}</div>
                <div>contentRect.w: {{ contentRect?.width }}</div>
                <div>contentRect.h: {{ contentRect?.height }}</div>

                <div>contentRect.x: {{ contentRect?.x }}</div>
                <div>contentRect.y: {{ contentRect?.y }}</div>

                <div>containerRect.t: {{ containerRect?.top }}</div>
                <div>containerRect.l: {{ containerRect?.left }}</div>

                <div>contentRect.t: {{ contentRect?.top }}</div>
                <div>contentRect.l: {{ contentRect?.left }}</div>

                <div>offsetWidth: {{ originalContentWidth }}</div>
                <div>offsetHeight: {{ originalContentHeight }}</div>

                <div>scaledWidth: {{ scaledWidth }}</div>
                <div>scaledHeight: {{ scaledHeight }}</div>

                <div>exceedsLeft: {{ exceedsLeft }}</div>
                <div>exceedsRight: {{ exceedsRight }}</div>
                <div>exceedsTop: {{ exceedsTop }}</div>
                <div>exceedsBottom: {{ exceedsBottom }}</div>
            </div>
        </div>
    </teleport>
</template>

<script setup lang="ts">
    import { translations } from "@/shared/localization/translations";
    import Hammer from "hammerjs";
    import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

    import VButton from "@/components/elements/VButton.vue";

    const props = defineProps<{
        class?: string;
        defaultScale?: number;
    }>();

    const MIN_SCALE = 1;
    const MAX_SCALE = 3;
    const SCALE_STEP = 0.1;
    const SHOW_DEBUG = false;

    const container = ref<HTMLElement | null>(null);
    const contentContainer = ref<HTMLElement | null>(null);

    const scale = ref(1);
    const translateX = ref(0);
    const translateY = ref(0);

    let hammer = null as HammerManager | null;
    const lastScale = ref(1);
    const lastTranslateX = ref(0);
    const lastTranslateY = ref(0);

    const isDragging = ref(false);

    // move boundaries
    const maxTranslate = ref({ x: 0, y: 0 });
    const minTranslate = ref({ x: 0, y: 0 });

    // container and content rects
    const containerRect = ref<DOMRect | null>(null);
    const contentRect = ref<DOMRect | null>(null);

    const scaledWidth = ref(0);
    const scaledHeight = ref(0);

    const originalContentWidth = ref(0);
    const originalContentHeight = ref(0);

    const isInitialized = ref(false);

    const areContainersSet = computed(() => containerRect.value && contentRect.value);
    const exceedsLeft = computed(
        () => areContainersSet.value && round(contentRect.value!.left) + 1 < round(containerRect.value!.left)
    );
    const exceedsRight = computed(
        () => areContainersSet.value && round(contentRect.value!.right) - 1 > round(containerRect.value!.right)
    );
    const exceedsTop = computed(
        () => areContainersSet.value && round(contentRect.value!.top) < round(containerRect.value!.top)
    );
    const exceedsBottom = computed(
        () => areContainersSet.value && round(contentRect.value!.bottom) > round(containerRect.value!.bottom)
    );

    const isChanged = computed(() => {
        return translateX.value !== 0 || translateY.value !== 0 || scale.value !== props.defaultScale;
    });

    const transformStyle = computed(() => ({
        transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
    }));

    // functions
    async function updateBounds() {
        if (container.value && contentContainer.value) {
            const content = contentContainer.value.firstElementChild as HTMLElement; // todo: returns always only first child
            if (!content) {
                return;
            }

            containerRect.value = container.value.getBoundingClientRect();
            contentRect.value = content.getBoundingClientRect();

            // original size of the content
            originalContentWidth.value = content.offsetWidth;
            originalContentHeight.value = content.offsetHeight;

            // scaled size of the content
            scaledWidth.value = round(originalContentWidth.value * scale.value);
            scaledHeight.value = round(originalContentHeight.value * scale.value);

            // calculate translation boundaries and apply constraints
            const translateXDifference = Math.abs(round((containerRect.value?.width - scaledWidth.value) / 2));
            minTranslate.value.x = -translateXDifference;
            maxTranslate.value.x = translateXDifference;

            const translateYDifference = Math.abs(round((containerRect.value?.height - scaledHeight.value) / 2));
            minTranslate.value.y = -translateYDifference;
            maxTranslate.value.y = translateYDifference;

            // constrain translation values within bounds
            translateX.value = clamp(translateX.value, minTranslate.value.x, maxTranslate.value.x);
            translateY.value = clamp(translateY.value, minTranslate.value.y, maxTranslate.value.y);

            await nextTick();
            contentRect.value = content.getBoundingClientRect();
        }
    }

    function reset() {
        scale.value = props.defaultScale ?? 1;
        translateX.value = 0;
        translateY.value = 0;
    }

    function fitToContainerHeight(percentage: number) {
        scale.value = round(((percentage / 100) * containerRect.value!.height) / originalContentHeight.value);
    }

    // event handlers
    function onWheel(event) {
        const newScale = scale.value + (event.deltaY > 0 ? -SCALE_STEP : SCALE_STEP);
        scale.value = round(Math.min(Math.max(newScale, MIN_SCALE), MAX_SCALE));
    }

    function onMouseDown() {
        isDragging.value = true;
    }

    function onMouseUp() {
        isDragging.value = false;
    }

    // helper functions
    function round(value: number) {
        return Math.round(value * 100) / 100;
    }

    function clamp(value: number, min: number, max: number) {
        return Math.min(Math.max(value, min), max);
    }

    watch(scale, () => {
        updateBounds();
    });

    onMounted(async () => {
        hammer = new Hammer(container.value!);
        hammer.get("pinch").set({ enable: true });
        hammer.get("pan").set({ direction: Hammer.DIRECTION_ALL });

        // handle pinch (two finger zoom)
        hammer.on("pinchstart", () => {
            lastScale.value = scale.value;
        });
        hammer.on("pinchmove", (ev) => {
            scale.value = Math.min(Math.max(lastScale.value * ev.scale, MIN_SCALE), MAX_SCALE);
        });
        // handle finger panning (move)
        hammer.on("panstart", () => {
            lastTranslateX.value = translateX.value;
            lastTranslateY.value = translateY.value;
        });
        hammer.on("panmove", (ev) => {
            translateX.value = lastTranslateX.value + ev.deltaX;
            translateY.value = lastTranslateY.value + ev.deltaY;
            updateBounds();
        });

        window.addEventListener("resize", updateBounds);
        document.addEventListener("mouseup", onMouseUp);

        updateBounds();
        reset();
        isInitialized.value = true;
    });

    onUnmounted(() => {
        if (hammer) {
            hammer.destroy();
        }
        document.removeEventListener("mouseup", onMouseUp);
        window.removeEventListener("resize", updateBounds);
    });

    defineExpose({
        fitToContainerHeight,
    });
</script>

<style lang="scss">
    .zoomable-container {
        width: 100%;
        height: 100vh;
        position: relative;
        overflow: hidden;
        box-sizing: border-box;
        cursor: grab;
        transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        .reset-btn {
            position: absolute;
            bottom: 12px;
            right: 16px;
            z-index: 1;
            transition: opacity 0.2s ease-in-out;
            opacity: 0;

            &.visible {
                opacity: 1;
            }
        }

        $shadow-color: rgba(0, 0, 0, 0.2);
        $shadow-left: inset 16px 0 8px -8px $shadow-color;
        $shadow-right: inset -16px 0 8px -8px $shadow-color;
        $shadow-top: inset 0 16px 8px -8px $shadow-color;
        $shadow-bottom: inset 0 -16px 8px -8px $shadow-color;

        // single edges
        &.exceeds-left {
            box-shadow: $shadow-left;
        }
        &.exceeds-right {
            box-shadow: $shadow-right;
        }
        &.exceeds-top {
            box-shadow: $shadow-top;
        }
        &.exceeds-bottom {
            box-shadow: $shadow-bottom;
        }

        // two parallel edges
        &.exceeds-left.exceeds-right {
            box-shadow: $shadow-left, $shadow-right;
        }
        &.exceeds-top.exceeds-bottom {
            box-shadow: $shadow-top, $shadow-bottom;
        }

        // two perpendicular edges
        &.exceeds-left.exceeds-top {
            box-shadow: $shadow-left, $shadow-top;
        }
        &.exceeds-left.exceeds-bottom {
            box-shadow: $shadow-left, $shadow-bottom;
        }
        &.exceeds-right.exceeds-top {
            box-shadow: $shadow-right, $shadow-top;
        }
        &.exceeds-right.exceeds-bottom {
            box-shadow: $shadow-right, $shadow-bottom;
        }

        // three edges
        &.exceeds-left.exceeds-right.exceeds-top {
            box-shadow: $shadow-left, $shadow-right, $shadow-top;
        }
        &.exceeds-left.exceeds-right.exceeds-bottom {
            box-shadow: $shadow-left, $shadow-right, $shadow-bottom;
        }
        &.exceeds-left.exceeds-top.exceeds-bottom {
            box-shadow: $shadow-left, $shadow-top, $shadow-bottom;
        }
        &.exceeds-right.exceeds-top.exceeds-bottom {
            box-shadow: $shadow-right, $shadow-top, $shadow-bottom;
        }

        // all four edges
        &.exceeds-left.exceeds-right.exceeds-top.exceeds-bottom {
            box-shadow: $shadow-left, $shadow-right, $shadow-top, $shadow-bottom;
        }
    }
    .zoomable-content {
        width: 100%;
        height: 100%;
        transform-origin: center center;
        display: flex;
        touch-action: none;
        overflow: hidden;
        cursor: "grab";

        &.dragging {
            cursor: grabbing;
        }

        & > div {
            flex: 1 1 auto;
        }
    }

    .zoomable-debug {
        position: fixed;
        bottom: 0;
        left: 0;
        background-color: white;
        padding: 20px;
        display: flex;
        flex-direction: row;
        gap: 10px;
    }
</style>
